import { useTranslation } from 'react-i18next'
import { type BilbransjeClass } from '../../types/bruktbilfinansiering'
import type { BuyerType } from '../BruktbilfinansieringPage'

export type DatePreset = 'forrige_maned' | 'hittil_i_ar' | 'annen'

/**
 * Earliest date bruktbilfinansiering has data for. Loan pairing went live
 * 2025-12-23 and the backend materialises transfers from this date onwards,
 * so it rejects earlier windows with 422 (bilpriserproff#285). Setting `min`
 * keeps the picker from offering a date that cannot work.
 */
export const MIN_DATE = '2026-01-01'

interface Props {
  startDate: string
  endDate: string
  onStartDateChange: (v: string) => void
  onEndDateChange: (v: string) => void
  maxDate: string | null
  preset: DatePreset | null
  onPresetClick: (p: DatePreset) => void
  selectedClasses: BilbransjeClass[]
  allClasses: readonly BilbransjeClass[]
  onToggleClass: (c: BilbransjeClass) => void
  showClassFilter?: boolean
  showBuyerTypeFilter?: boolean
  selectedBuyerTypes?: BuyerType[]
  onToggleBuyerType?: (bt: BuyerType) => void
  onResetBuyerTypes?: () => void
  onApply: () => void
  isDirty: boolean
}

function classLabelKey(c: BilbransjeClass) {
  switch (c) {
    case 'BILBRANSJE_TO_PERSON': return 'buyerTypes.PERSON' as const
    case 'BILBRANSJE_TO_FIRMA': return 'buyerTypes.FIRMA' as const
    case 'BILBRANSJE_TO_BILUTLEIE': return 'buyerTypes.BILUTLEIE' as const
  }
}

const PRESETS = [
  { key: 'hittil_i_ar', labelKey: 'filterBar.presets.hittilIAr' },
  { key: 'forrige_maned', labelKey: 'filterBar.presets.forrigeManed' },
  { key: 'annen', labelKey: 'filterBar.presets.annen' },
] as const satisfies readonly { key: DatePreset; labelKey: string }[]

const BUYER_TYPE_LABEL_KEYS = {
  PERSON: 'buyerTypes.PERSON',
  FIRMA: 'buyerTypes.FIRMA',
  BILUTLEIE: 'buyerTypes.BILUTLEIE',
} as const satisfies Record<BuyerType, string>

export default function BruktbilfinansieringFilterBar({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  maxDate,
  preset,
  onPresetClick,
  selectedClasses,
  allClasses,
  onToggleClass,
  showClassFilter = true,
  showBuyerTypeFilter = false,
  selectedBuyerTypes,
  onToggleBuyerType,
  onResetBuyerTypes,
  onApply,
  isDirty,
}: Props) {
  const { t } = useTranslation('bbf')
  const showCustomRange = preset === 'annen' || preset === null

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 flex flex-wrap items-center gap-4">
      {/* Date preset buttons */}
      <div className="flex gap-2">
        {PRESETS.map(({ key, labelKey }) => (
          <button
            key={key}
            onClick={() => onPresetClick(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              preset === key
                ? 'bg-primary-600 text-white shadow-xs'
                : 'bg-gray-100 dark:bg-dark-card text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-border'
            }`}
          >
            {t(labelKey)}
          </button>
        ))}
      </div>

      {/* Custom date range pickers */}
      {showCustomRange && (
        <>
          <div className="flex items-center gap-2">
            <label htmlFor="start-date" className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('shared.fromDate')}</label>
            <input
              id="start-date"
              type="date"
              value={startDate}
              min={MIN_DATE}
              max={maxDate && endDate ? (maxDate < endDate ? maxDate : endDate) : (maxDate ?? endDate ?? undefined)}
              onChange={e => onStartDateChange(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-sm px-2 py-1 text-sm bg-white dark:bg-gray-700 dark:text-white"
              aria-label={t('shared.fromDate')}
            />
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="end-date" className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('shared.toDate')}</label>
            <input
              id="end-date"
              type="date"
              value={endDate}
              min={startDate && startDate > MIN_DATE ? startDate : MIN_DATE}
              max={maxDate ?? undefined}
              onChange={e => onEndDateChange(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-sm px-2 py-1 text-sm bg-white dark:bg-gray-700 dark:text-white"
              aria-label={t('shared.toDate')}
            />
          </div>
        </>
      )}

      {/* Class filter */}
      {showClassFilter && (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('filterBar.type')}</span>
          {allClasses.map(c => (
            <label key={c} className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300" htmlFor={`class-${c}`}>
              <input
                id={`class-${c}`}
                type="checkbox"
                checked={selectedClasses.includes(c)}
                onChange={() => onToggleClass(c)}
                className="rounded-sm"
              />
              {t(classLabelKey(c))}
            </label>
          ))}
        </div>
      )}

      {/* Buyer type filter (Forhandlere tab) */}
      {showBuyerTypeFilter && selectedBuyerTypes && onToggleBuyerType && (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('filterBar.type')}</span>
          {(['PERSON', 'FIRMA', 'BILUTLEIE'] as const).map(bt => (
            <label key={bt} className="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={selectedBuyerTypes.includes(bt)}
                onChange={() => onToggleBuyerType(bt)}
                className="rounded-sm"
              />
              {t(BUYER_TYPE_LABEL_KEYS[bt])}
            </label>
          ))}
          {selectedBuyerTypes.length < 3 && onResetBuyerTypes && (
            <button onClick={onResetBuyerTypes} className="text-xs text-primary-600 hover:underline dark:text-primary-400">
              {t('shared.reset')}
            </button>
          )}
        </div>
      )}

      {/* Apply button */}
      <button
        onClick={onApply}
        disabled={!isDirty}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
          isDirty
            ? 'bg-primary-600 text-white shadow-xs hover:bg-primary-700'
            : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
        }`}
      >
        {t('filterBar.apply')}
      </button>
    </div>
  )
}
